import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { Button } from "@moe/oss/ui/button";
import { LogoButton } from "@moe/oss/ui/logo-button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@moe/oss/ui/select";
import { useDebounceFn } from "@moe/priv/hooks/use-debounce-fn";
import { useOnClickOutside } from "@moe/priv/hooks/use-on-click-outside";
import { CharacterService } from "@moe/priv/model/character";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { AuthButtons } from "@web/components/AuthButtons";
import { SearchBar } from "@web/components/search/SearchBar";
import { useParseCharacterSearch } from "@web/components/search/use-parse-character-search";
import { useHideOnScroll } from "@web/hooks/use-hide-on-scroll";
import { exploreCharactersQK } from "@web/route-services/index/Explore";
import { NSFWSelector } from "@web/route-services/index/NSFWSelector";
import { TagRow } from "@web/route-services/index/TagRow";
import { TimeFilterSelect } from "@web/route-services/index/TimeFilterSelect";
import { useAppContext } from "@web/route-services/root/AppContext";
import { Route } from "@web/routes";
import { motion } from "motion/react";
import { useState } from "react";
import { FaDice } from "react-icons/fa";
import { AnimatedLogoText } from "./AnimatedLogoText";
type Ranking = CharacterService.Ranking;

export function Header() {
  const { session } = useAppContext();
  const [isSearchInputActive, setIsSearchInputActive] = useState(false);
  const nav = useNavigate({ from: Route.fullPath });
  const shouldHide = useHideOnScroll({ sensitivity: 20, yThreshold: 300 });
  const { isDesktopScreen } = useAppContext();
  const [cachedMobileSearch, setCachedMobileSearch] = useState<string | undefined>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { parseSearch } = useParseCharacterSearch();

  /**
   * Disable searching when clicking outside of the search bar
   */
  const searchContainerRef = useOnClickOutside<HTMLDivElement>(() => {
    if (isSearchInputActive) setIsSearchInputActive(false);
  });

  const search = (query: string, excludeTags?: boolean) => {
    if (!query) {
      handleClear();
      return;
    }

    const parsed = parseSearch(query);
    if (parsed.type === "link") {
      navigate({
        to: "/character/$characterId",
        params: { characterId: parsed.characterID }
      });
    } else if (parsed.type === "query") {
      if (!parsed.tags.length && !parsed.text) {
        return;
      }

      nav({
        search: (prev) => ({
          ...prev,
          page: 1,
          searchText: parsed.text,
          tags: excludeTags ? prev.tags : [...new Set([...parsed.tags])]
        })
      });
    }
  };

  const debouncedSearch = useDebounceFn(search, 300);

  const handleSearchInput = (query: string) => {
    debouncedSearch(query);
    if (!isDesktopScreen) setCachedMobileSearch(query);
  };

  const handleSearchSubmit = (query: string) => {
    search(query);
    if (!isDesktopScreen) {
      setCachedMobileSearch(query);
      setIsSearchInputActive(false);
    }
  };

  const handleClear = (clearedInput?: string) => {
    nav({ search: {} });
    if (clearedInput === "") setIsSearchInputActive(false);
  };

  if (isDesktopScreen) {
    return (
      <div className="mb-8 flex w-full space-x-8 pr-2.5">
        <Link to="/" className="text-4xl font-bold">
          <AnimatedLogoText logoText="anime.gf" />
        </Link>
        <div className="grow"></div>
        {!session && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.3 }}>
            <AuthButtons />
          </motion.div>
        )}
        <SearchBar onSearch={handleSearchSubmit} onInputChange={handleSearchInput} onClear={handleClear} />
      </div>
    );
  }

  // Only hide the header when we're not searching
  const isVisible = !shouldHide || isSearchInputActive;
  return (
    <motion.div
      className="pointer-events-auto fixed left-0 right-0 top-0 z-10 flex w-screen flex-col bg-black/70 font-medium backdrop-blur-lg"
      initial={false}
      animate={{
        transform: isVisible ? "translateY(0)" : "translateY(-100%)",
        opacity: isVisible ? 1 : 0
      }}
      transition={{
        transform: { duration: isVisible ? 0.28 : 0.2, ease: "easeIn" },
        opacity: { duration: isVisible ? 0.2 : 0.2, ease: "easeIn" }
      }}
    >
      {/* Explore Options */}
      <div className="flex h-11 items-center justify-between">
        {!isSearchInputActive && (
          <>
            <LogoButton className="ml-4 mr-6 h-7 w-7 shrink-0" onClick={() => nav({ to: "/" })} />
            <div className="scroll-hidden z-10 flex flex-1 items-center space-x-4 overflow-x-auto overflow-y-hidden">
              <Select
                defaultValue="message_count"
                onValueChange={(v) => {
                  nav({ search: (prev) => ({ ...prev, ranking: v as Ranking, order: "desc", pageSize: undefined }) });
                }}
              >
                <SelectTrigger className="text-tx-primary h-full w-fit justify-start border-none bg-transparent px-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="message_count">Top</SelectItem>
                  <SelectItem value="recency">New</SelectItem>
                  <SelectItem value="hotness">Hot</SelectItem>
                  <SelectItem value="random">Random</SelectItem>
                  {session && <SelectItem value="liked">Liked</SelectItem>}
                </SelectContent>
              </Select>
              <TimeFilterSelect />
              <NSFWSelector />
              <motion.button whileTap={{ scale: 0.8, rotate: 180 }} transition={{ duration: 0.2 }}>
                <FaDice
                  onClick={() => {
                    nav({ search: (prev) => ({ ...prev, ranking: "random", order: "desc", pageSize: 1 }) });
                    queryClient.invalidateQueries({ queryKey: [exploreCharactersQK] });
                  }}
                  className="text-tx-tertiary hover:text-tx-primary size-5 shrink-0 font-medium transition duration-200 ease-out"
                />
              </motion.button>
            </div>
          </>
        )}

        {/* Search Button */}
        {!isSearchInputActive && (
          <Button
            className="mr-2 p-2.5"
            variant="clear"
            onClick={() => {
              setIsSearchInputActive(true);
            }}
          >
            <MagnifyingGlassIcon className="text-tx-tertiary" />
          </Button>
        )}

        {/* Search Bar */}
        {isSearchInputActive && (
          <SearchBar
            initialValue={cachedMobileSearch}
            className="h-11 w-full rounded-none border-none bg-transparent"
            onClear={handleClear}
            onSearch={handleSearchSubmit}
            onInputChange={handleSearchInput}
            ref={searchContainerRef}
            // Focus the search input when the search bar is visible
            inputRef={(node) => {
              if (node) node.focus();
            }}
          />
        )}
      </div>
    </motion.div>
  );
}
